import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGoogleConnectUrl,
  useGoogleConnectionPing,
  useGoogleConnections,
  useGoogleOAuthConfig,
  useImportGoogleLocations,
  useLocations,
  useSaveGoogleOAuthConfig,
} from '../lib/hooks';
import { getApiBaseUrl } from '../lib/api';
import {
  Button,
  CopyChip,
  Field,
  Icon,
  InlineAlert,
  PageHeader,
  SkeletonList,
  useSavedFlag,
  useToast,
} from '../components/ui';

export function OnboardingPage() {
  const { toast } = useToast();
  const oauthConfig = useGoogleOAuthConfig();
  const saveOauthConfig = useSaveGoogleOAuthConfig();
  const connections = useGoogleConnections();
  const connectUrl = useGoogleConnectUrl();
  const pingMutation = useGoogleConnectionPing();
  const importMutation = useImportGoogleLocations();
  const locationsQuery = useLocations({ page: 1, pageSize: 1 });

  const [oauthForm, setOauthForm] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: '',
  });
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [credentialsSaved, flashCredentialsSaved] = useSavedFlag();
  const [pingingId, setPingingId] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);

  const redirectUri = `${getApiBaseUrl()}/v1/onboarding/google/callback`;

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.data?.source !== 'gmb-google-oauth') {
        return;
      }

      if (event.data.ok) {
        toast({
          title: 'Google account connected',
          description: 'Great — now import your locations in step 3 below.',
        });
        connections.refetch();
      } else {
        toast({
          variant: 'error',
          title: 'Connection did not complete',
          description: 'The Google window closed before finishing. Click “Connect Google account” to try again.',
        });
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [connections, toast]);

  const connectionRows = (connections.data || []) as any[];
  const connectedCount = connectionRows.length;
  const oauthConfigured = Boolean(oauthConfig.data?.configured);
  const hasLocations = (locationsQuery.data?.total ?? locationsQuery.data?.rows?.length ?? 0) > 0;

  const stepStates = useMemo(() => {
    const oauthDone = oauthConfigured;
    const connectDone = connectedCount > 0;
    const importDone = hasLocations && connectDone;
    const allDone = oauthDone && connectDone && importDone;
    return [
      { key: 'credentials', label: 'Google credentials', done: oauthDone },
      { key: 'connect', label: 'Connect account', done: connectDone },
      { key: 'import', label: 'Import locations', done: importDone },
      { key: 'ready', label: 'Start publishing', done: allDone },
    ];
  }, [oauthConfigured, connectedCount, hasLocations]);

  const currentIndex = stepStates.findIndex((step) => !step.done);

  return (
    <>
      <PageHeader
        title="Connect Google Business Profile"
        description="Four steps, once per agency. After this, posts publish themselves on schedule — no technical knowledge needed."
      />

      <section className="card">
        <div className="setup-progress">
          {stepStates.map((step, index) => (
            <div
              key={step.key}
              className={`setup-progress-step ${step.done ? 'done' : ''} ${index === currentIndex ? 'current' : ''}`}
            >
              <div className="setup-progress-bar" />
              <span>
                {step.done && <Icon.Check size={13} />}
                {index + 1}. {step.label}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Step 1 — OAuth credentials                                    */}
      {/* ------------------------------------------------------------ */}
      <section className="card">
        <div className={`setup-step ${oauthConfigured ? 'done' : currentIndex === 0 ? 'current' : ''}`}>
          <span className="setup-step-num">{oauthConfigured ? <Icon.Check size={16} /> : '1'}</span>
          <div className="setup-step-body">
            <h3>
              Add your Google app credentials
              {oauthConfigured && <span className="badge success"><span className="dot" />Done</span>}
            </h3>
            <p className="muted" style={{ margin: 0 }}>
              Google requires every agency to have its own (free) “app” that asks clients for permission to post.
              You create it once in Google Cloud, copy two values here, and never touch it again.
            </p>

            <details className="help-details">
              <summary>
                <Icon.Help size={15} />
                Show me exactly how to get these values (5–10 minutes)
                <Icon.Chevron className="chev" />
              </summary>
              <div className="help-details-body">
                <ol>
                  <li>
                    Open{' '}
                    <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer">
                      console.cloud.google.com <Icon.External size={11} />
                    </a>{' '}
                    and sign in with your agency&rsquo;s Google account (any Google account works).
                  </li>
                  <li>
                    At the top of the page, click the project selector and choose <strong>New project</strong>. Name it
                    anything you like (for example, <code>My Agency Posting</code>) and click <strong>Create</strong>.
                  </li>
                  <li>
                    With your new project selected, open the menu (☰) →{' '}
                    <strong>APIs &amp; Services → Library</strong>. Search for{' '}
                    <code>Google My Business API</code> and click <strong>Enable</strong>. Do the same for{' '}
                    <code>My Business Business Information API</code> and{' '}
                    <code>My Business Account Management API</code>.
                  </li>
                  <li>
                    Go to <strong>APIs &amp; Services → OAuth consent screen</strong>. Choose{' '}
                    <strong>External</strong>, click <strong>Create</strong>, fill in your agency name and your email
                    in the required fields, and keep clicking <strong>Save and continue</strong> until it is done. You
                    can skip everything optional.
                  </li>
                  <li>
                    Go to <strong>APIs &amp; Services → Credentials</strong>, click{' '}
                    <strong>+ Create credentials → OAuth client ID</strong>, and choose{' '}
                    <strong>Web application</strong> as the type.
                  </li>
                  <li>
                    Under <strong>Authorized redirect URIs</strong>, click <strong>+ Add URI</strong> and paste exactly
                    this address:
                    <div style={{ marginTop: 8 }}>
                      <CopyChip value={redirectUri} />
                    </div>
                  </li>
                  <li>
                    Click <strong>Create</strong>. Google shows you a <strong>Client ID</strong> and a{' '}
                    <strong>Client secret</strong> — copy both into the form below and click{' '}
                    <strong>Save credentials</strong>.
                  </li>
                  <li>
                    One more thing for publishing at scale: Google asks businesses to request access to the Business
                    Profile API once via{' '}
                    <a
                      href="https://developers.google.com/my-business/content/prereqs#request-access"
                      target="_blank"
                      rel="noreferrer"
                    >
                      their access request form <Icon.External size={11} />
                    </a>
                    . Approval usually takes a few days and you only do it once.
                  </li>
                </ol>
              </div>
            </details>

            {oauthConfigured && oauthConfig.data?.clientIdMasked && (
              <InlineAlert variant="success">
                Credentials are saved and active (Client ID {oauthConfig.data.clientIdMasked}). Submitting the form
                below replaces them.
              </InlineAlert>
            )}

            <form
              className="grid"
              onSubmit={async (event) => {
                event.preventDefault();
                setOauthError(null);
                try {
                  await saveOauthConfig.mutateAsync({
                    clientId: oauthForm.clientId,
                    clientSecret: oauthForm.clientSecret,
                    redirectUri: oauthForm.redirectUri || undefined,
                  });
                  setOauthForm((current) => ({ ...current, clientSecret: '' }));
                  oauthConfig.refetch();
                  flashCredentialsSaved();
                  toast({ title: 'Credentials saved', description: 'Now connect your Google account in step 2.' });
                } catch (err) {
                  setOauthError((err as Error).message);
                }
              }}
            >
              <div className="form-grid">
                <Field label="Client ID" hint="Ends in “.apps.googleusercontent.com”.">
                  <input
                    value={oauthForm.clientId}
                    onChange={(event) => setOauthForm((current) => ({ ...current, clientId: event.target.value }))}
                    placeholder="1234567890-xxxxx.apps.googleusercontent.com"
                    required
                  />
                </Field>
                <Field label="Client secret" hint="Stored encrypted. Never shown again after saving.">
                  <input
                    type="password"
                    value={oauthForm.clientSecret}
                    onChange={(event) => setOauthForm((current) => ({ ...current, clientSecret: event.target.value }))}
                    placeholder="GOCSPX-…"
                    required
                  />
                </Field>
                <Field label="Redirect URI (optional)" hint="Leave empty to use the standard one shown in the guide.">
                  <input
                    value={oauthForm.redirectUri}
                    onChange={(event) => setOauthForm((current) => ({ ...current, redirectUri: event.target.value }))}
                    placeholder={redirectUri}
                  />
                </Field>
              </div>

              {oauthError && <InlineAlert variant="danger">{oauthError}</InlineAlert>}

              <div>
                <Button
                  type="submit"
                  loading={saveOauthConfig.isPending}
                  loadingText="Saving…"
                  saved={credentialsSaved}
                  savedText="Saved"
                >
                  Save credentials
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Step 2 — Connect account                                      */}
      {/* ------------------------------------------------------------ */}
      <section className="card">
        <div className={`setup-step ${connectedCount > 0 ? 'done' : currentIndex === 1 ? 'current' : ''}`}>
          <span className="setup-step-num">{connectedCount > 0 ? <Icon.Check size={16} /> : '2'}</span>
          <div className="setup-step-body">
            <h3>
              Connect the Google account that manages the profiles
              {connectedCount > 0 && (
                <span className="badge success">
                  <span className="dot" />
                  {connectedCount} connected
                </span>
              )}
            </h3>
            <p className="muted" style={{ margin: 0 }}>
              Click the button below — a Google sign-in window opens. Sign in with the account that{' '}
              <strong>owns or manages</strong> the Google Business Profiles (the one you use at{' '}
              business.google.com), then click <strong>Allow</strong>. You can connect several accounts if your
              clients&rsquo; profiles live under different ones.
            </p>

            <details className="help-details">
              <summary>
                <Icon.Help size={15} />
                Not sure which account to use?
                <Icon.Chevron className="chev" />
              </summary>
              <div className="help-details-body">
                <ol>
                  <li>
                    Open{' '}
                    <a href="https://business.google.com/locations" target="_blank" rel="noreferrer">
                      business.google.com/locations <Icon.External size={11} />
                    </a>{' '}
                    in another tab.
                  </li>
                  <li>
                    If you can see the business profiles you want to automate listed there, you are signed in with the
                    right account — use that same account here.
                  </li>
                  <li>
                    If you don&rsquo;t see them, the profile owner needs to add you first: they open their Business
                    Profile, go to <strong>Settings → People and access → Add</strong>, and add your email as{' '}
                    <strong>Manager</strong>. Then come back and connect.
                  </li>
                </ol>
              </div>
            </details>

            <div className="inline">
              <Button
                variant="secondary"
                icon={<Icon.Google size={16} />}
                onClick={async () => {
                  try {
                    const payload = await connectUrl.mutateAsync();
                    window.open(payload.url, '_blank', 'noopener,noreferrer,width=520,height=780');
                  } catch (err) {
                    toast({
                      variant: 'error',
                      title: 'Could not open Google sign-in',
                      description: (err as Error).message,
                    });
                  }
                }}
                loading={connectUrl.isPending}
                loadingText="Opening Google…"
                disabled={!oauthConfigured}
              >
                Connect Google account
              </Button>
              {!oauthConfigured && <span className="muted">Finish step 1 first.</span>}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Step 3 — Import locations                                     */}
      {/* ------------------------------------------------------------ */}
      <section className="card">
        <div className={`setup-step ${hasLocations && connectedCount > 0 ? 'done' : currentIndex === 2 ? 'current' : ''}`}>
          <span className="setup-step-num">{hasLocations && connectedCount > 0 ? <Icon.Check size={16} /> : '3'}</span>
          <div className="setup-step-body">
            <h3>Import your business locations</h3>
            <p className="muted" style={{ margin: 0 }}>
              We will fetch every business location your connected account can manage and add them to your workspace.
              Nothing is published yet — you choose schedules first.
            </p>
            <div className="inline">
              <Button
                onClick={async () => {
                  try {
                    const response = await importMutation.mutateAsync({});
                    const summary = (response as any)?.summary;
                    toast({
                      title: 'Locations imported',
                      description: `${summary?.created ?? 0} new, ${summary?.updated ?? 0} updated. Review them on the Locations page.`,
                    });
                    locationsQuery.refetch();
                  } catch (err) {
                    toast({ variant: 'error', title: 'Import failed', description: (err as Error).message });
                  }
                }}
                loading={importMutation.isPending && importingId === null}
                loadingText="Importing…"
                disabled={connectedCount === 0}
              >
                Import locations
              </Button>
              {connectedCount === 0 && <span className="muted">Connect a Google account first.</span>}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Step 4 — Ready                                                */}
      {/* ------------------------------------------------------------ */}
      <section className="card">
        <div className={`setup-step ${stepStates[3]?.done ? 'done' : currentIndex === 3 ? 'current' : ''}`}>
          <span className="setup-step-num">{stepStates[3]?.done ? <Icon.Check size={16} /> : '4'}</span>
          <div className="setup-step-body">
            <h3>Set a schedule and publish your first post</h3>
            <p className="muted" style={{ margin: 0 }}>
              Open <Link to="/locations">Locations</Link>, pick a business, choose how many posts per week it should
              get, and fill in the short content brief so posts sound like the owner wrote them. Then press{' '}
              <strong>Publish a post now</strong> to see your first post go live within a couple of minutes.
            </p>
            <div>
              <Link to="/locations">
                <Button variant={stepStates[3]?.done ? 'primary' : 'secondary'} icon={<Icon.Pin size={15} />}>
                  Open Locations
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/* Connected accounts health                                     */}
      {/* ------------------------------------------------------------ */}
      <section className="card">
        <h3>Connected Google accounts</h3>
        <p className="card-sub">
          Run a quick check anytime to confirm a connection is still healthy. If one needs attention, reconnect it in
          step 2.
        </p>

        {connections.isLoading ? (
          <SkeletonList rows={2} height={86} />
        ) : connectionRows.length === 0 ? (
          <InlineAlert variant="info">
            No Google accounts connected yet. Complete steps 1 and 2 above — it takes about ten minutes the first
            time.
          </InlineAlert>
        ) : (
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))' }}>
            {connectionRows.map((connection: any) => (
              <div key={connection.id} className="connection-card">
                <div className="inline" style={{ justifyContent: 'space-between' }}>
                  <strong>{connection.displayName || connection.email || 'Google account'}</strong>
                  <span className={`badge ${connection.healthy ? 'success' : 'danger'}`}>
                    <span className="dot" />
                    {connection.healthy ? 'Healthy' : 'Needs attention'}
                  </span>
                </div>
                <span className="muted">{connection.email || 'No email available'}</span>
                <span className="muted">
                  Last checked:{' '}
                  {connection.lastHealthCheckAt ? new Date(connection.lastHealthCheckAt).toLocaleString() : 'Never'}
                </span>
                <div className="inline" style={{ marginTop: 6 }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Icon.Refresh size={13} />}
                    onClick={() => {
                      setPingingId(connection.id);
                      pingMutation.mutate(connection.id, {
                        onSuccess: () => {
                          setPingingId(null);
                          toast({ title: 'Connection is healthy', description: connection.email || undefined });
                          connections.refetch();
                        },
                        onError: (error) => {
                          setPingingId(null);
                          toast({
                            variant: 'error',
                            title: 'Connection check failed',
                            description: (error as Error).message,
                          });
                        },
                      });
                    }}
                    loading={pingMutation.isPending && pingingId === connection.id}
                    loadingText="Checking…"
                  >
                    Check connection
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setImportingId(connection.id);
                      importMutation.mutate(
                        { connectionId: connection.id },
                        {
                          onSuccess: (response: any) => {
                            setImportingId(null);
                            const summary = response?.summary;
                            toast({
                              title: 'Import finished',
                              description: `${summary?.created ?? 0} new, ${summary?.updated ?? 0} updated.`,
                            });
                            locationsQuery.refetch();
                          },
                          onError: (error) => {
                            setImportingId(null);
                            toast({ variant: 'error', title: 'Import failed', description: (error as Error).message });
                          },
                        },
                      );
                    }}
                    loading={importMutation.isPending && importingId === connection.id}
                    loadingText="Importing…"
                  >
                    Import from this account
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
